import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useWaitOverlayStore } from '@apply/components';
import { FeatureType } from '@apply/constants';
import { useAuthentication } from '../Login/hooks/useAuthentication';
import { Navigate } from 'react-router';
import ValidationGroup from 'devextreme-react/validation-group';
import { WizardLayout } from './wizard/WizardLayout';
import { WizardFooter } from './wizard/WizardFooter';
import { useWizardNavigation } from './wizard/useWizardNavigation';
import { StepApi } from './wizard/stepApi';
import { WizardStepId, findStepById, nextStep, stepUrl } from './wizard/steps';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import {
    BP_MUTATION_SCOPE,
    isBusinessProfileQueryKey,
    useBorrowerEntitiesQuery,
} from './queries/businessProfileQueries';
import { StepErrorBanner } from './components/StepErrorBanner';
import { BusinessProfileStep } from './steps/BusinessProfileStep';
import { BorrowerEntityStep } from './steps/BorrowerEntityStep';
import { OwnersStep } from './steps/OwnersStep';
import { OtherOfficersStep } from './steps/OtherOfficersStep';
import { KeyEmployeesStep } from './steps/KeyEmployeesStep';
import { ReferencesStep } from './steps/ReferencesStep';
import { ReviewStep } from './steps/ReviewStep';

const SAVING_LABEL = 'Saving your changes...';
const LOADING_LABEL = 'Loading...';

export const BusinessProfileWizard: React.FC = () => {
    const { isFeatureEnabled } = useAuthentication();
    const stepApi = useRef<StepApi>({ save: async () => undefined });
    const validationGroupRef = useRef<ValidationGroup>(null);
    const [canAdvance, setCanAdvance] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<unknown>(null);

    const runNavigation = useCallback(async (work: () => Promise<void>) => {
        setIsSaving(true);
        try {
            await work();
        } finally {
            setIsSaving(false);
        }
    }, []);

    const flushStep = useCallback(async (): Promise<boolean> => {
        if (!stepApi.current.isDirty) {
            return true;
        }

        setSaveError(null);
        try {
            await stepApi.current.save();
            return true;
        } catch (error) {
            setSaveError(error);
            return false;
        }
    }, []);

    const {
        currentStep,
        isCanonicalPath,
        resumeStep,
        completedStepIds,
        isLoading,
        businessProfileId,
        activeEntityId,
        goToStep,
        goBack,
        skip,
        saveAndExit,
        completeAndAdvance,
        hasPrevious,
    } = useWizardNavigation({ flushStep });

    const { data: entities } = useBorrowerEntitiesQuery(businessProfileId);
    const activeEntity = entities?.find((e) => e.id === activeEntityId) ?? entities?.[0];
    const entityName = activeEntity?.legalName ?? 'this borrower entity';

    const isReading = useIsFetching({ predicate: (query) => isBusinessProfileQueryKey(query.queryKey) }) > 0;
    const isWriting = useIsMutating({ mutationKey: [BP_MUTATION_SCOPE] }) > 0;
    const isBusy = isSaving || isWriting || isReading;

    useEffect(() => {
        const label = isSaving || isWriting ? SAVING_LABEL : LOADING_LABEL;
        useWaitOverlayStore.getState().setWaiting(isBusy, label);
    }, [isBusy, isSaving, isWriting]);

    // Leaving mid-operation must not strand the overlay over the next page
    useEffect(() => () => useWaitOverlayStore.getState().setWaiting(false), []);

    // A failure belongs to the step it happened on, not the one we moved to
    useEffect(() => {
        setSaveError(null);
    }, [currentStep.id]);

    const registerStep = useCallback((api: StepApi) => {
        stepApi.current = api;
        setCanAdvance(api.canAdvance ?? true);
    }, []);

    const handleNext = useCallback(async () => {
        // Don't show input validation errors on load
        const result = validationGroupRef.current?.instance?.validate();
        if (result && result.isValid === false) {
            return;
        }

        setSaveError(null);
        await runNavigation(async () => {
            try {
                await stepApi.current.save();
                await completeAndAdvance();
            } catch (error) {
                setSaveError(error);
            }
        });
    }, [completeAndAdvance, runNavigation]);

    const handleStepClick = useCallback(
        (stepId: WizardStepId) => {
            const target = findStepById(stepId);
            if (target) {
                void runNavigation(() => goToStep(target));
            }
        },
        [goToStep, runNavigation],
    );

    if (!isFeatureEnabled(FeatureType.BusinessProfileWizard)) {
        return (
            <Navigate
                to='/home'
                replace
            />
        );
    }

    if (isLoading) {
        return <div className='bp-step__loading'>Loading…</div>;
    }

    if (!isCanonicalPath) {
        // Resume where the user left off rather than restarting
        return (
            <Navigate
                to={stepUrl(resumeStep)}
                replace
            />
        );
    }

    const renderStep = () => {
        switch (currentStep.id) {
            case WizardStepId.BusinessProfile:
                return <BusinessProfileStep onRegister={registerStep} />;
            case WizardStepId.BorrowerEntity:
                return (
                    <BorrowerEntityStep
                        businessProfileId={businessProfileId}
                        activeEntityId={activeEntityId}
                        onRegister={registerStep}
                    />
                );
            case WizardStepId.Owners:
                return (
                    <OwnersStep
                        businessProfileId={businessProfileId}
                        activeEntityId={activeEntityId}
                        entityName={entityName}
                        onRegister={registerStep}
                    />
                );
            case WizardStepId.OtherOfficers:
                return (
                    <OtherOfficersStep
                        activeEntityId={activeEntityId}
                        onRegister={registerStep}
                    />
                );
            case WizardStepId.KeyEmployees:
                return (
                    <KeyEmployeesStep
                        activeEntityId={activeEntityId}
                        onRegister={registerStep}
                    />
                );
            case WizardStepId.References:
                return (
                    <ReferencesStep
                        businessProfileId={businessProfileId}
                        onRegister={registerStep}
                    />
                );
            case WizardStepId.Review:
                return (
                    <ReviewStep
                        businessProfileId={businessProfileId}
                        onEditNode={() => {
                            const owners = findStepById(WizardStepId.Owners);
                            if (owners) {
                                void runNavigation(() => goToStep(owners));
                            }
                        }}
                        onRegister={registerStep}
                    />
                );
        }
    };

    const heading =
        currentStep.id === WizardStepId.Owners
            ? `Owners of ${entityName}`
            : currentStep.heading ?? currentStep.label;

    const upcoming = nextStep(currentStep.id);
    // No next step means this is the end of the wizard, which finishes by completing the session.
    const nextLabel = upcoming
        ? `Continue to ${upcoming.label}`
        : currentStep.id === WizardStepId.Review
          ? 'Save Organization'
          : 'Save and Finish';

    return (
        <WizardLayout
            currentStepId={currentStep.id}
            completedStepIds={completedStepIds}
            onStepClick={handleStepClick}
            onSaveAndExit={() => void runNavigation(saveAndExit)}
            title={heading}
            titleIcon={currentStep.icon && <FontAwesomeIcon icon={currentStep.icon} />}
            footer={
                <WizardFooter
                    showBack={hasPrevious}
                    onBack={() => void runNavigation(goBack)}
                    onNext={handleNext}
                    onSkip={currentStep.canSkip ? () => void runNavigation(skip) : undefined}
                    nextLabel={nextLabel}
                    canGoNext={canAdvance}
                    isSaving={isSaving}
                />
            }
        >
            {saveError != null && (
                <StepErrorBanner
                    error={saveError}
                    onDismiss={() => setSaveError(null)}
                />
            )}
            <ValidationGroup ref={validationGroupRef}>{renderStep()}</ValidationGroup>
        </WizardLayout>
    );
};

export default BusinessProfileWizard;
